// "Ask me later", remembered.
//
// ⚠️ AN HOUR, NOT FOR EVER, AND NOT FOR THE SESSION. The three obvious
// alternatives are all wrong in ways the register pays for:
//
//   • Dismiss for ever → one stray click on a Monday morning and that person's
//     attendance is never asked for again. Nothing tells anybody; the row is
//     simply missing at month end.
//   • Dismiss for the page → it reappears on the next navigation, which is
//     three seconds later. That is not a dismissal, it is a stutter, and it is
//     how people learn to answer falsely to make a dialog go away.
//   • sessionStorage → gone when the tab closes, so the laptop reopened after
//     lunch asks again immediately. Same stutter, slower.
//
// ⚠️ HOW LONG IS THE FIRM'S CALL, NOT THIS FILE'S. The length arrives with
// the prompt (`AttendanceSetting.dayPromptSnoozeMinutes`, set on the
// Attendance settings screen) — a practice whose staff are in and out of court
// all morning wants two hours, one that has had three people forget this week
// wants twenty minutes, and neither should need a deploy. The constant below
// is only the fallback for a server that did not say.
//
// ⚠️ EVERY ACCESS IS WRAPPED. localStorage throws outright in a locked-down
// browser or a private window with site data blocked, and an attendance dialog
// must degrade to "always ask" rather than taking the page down with it.

export const SNOOZE_MINUTES_FALLBACK = 60;

const PREFIX = 'att-day-prompt';

/**
 * ⚠️ KEYED BY DATE AND BY KIND, deliberately. A morning snoozed must not also
 * silence the evening — they are different questions about different halves of
 * the day — and yesterday's dismissal must not carry into today, which is the
 * bug that would make the whole feature quietly stop working after its first
 * week.
 *
 * Not keyed by user: the key lives in one browser profile, and two people
 * sharing a login is not a case this needs to be right for. Signing out and
 * back in as somebody else inherits at most one hour of silence.
 */
export const snoozeKey = (kind, dateKey) => `${PREFIX}:${kind}:${dateKey}`;

export function isSnoozed(key, now = Date.now()) {
  try {
    const until = Number(window.localStorage.getItem(key));
    return Number.isFinite(until) && until > now;
  } catch {
    return false;
  }
}

export function snooze(key, minutes = SNOOZE_MINUTES_FALLBACK, now = Date.now()) {
  try {
    window.localStorage.setItem(key, String(now + minutes * 60000));
    // Yesterday's keys, cleared on the way past. Nothing else ever visits this
    // corner of storage, and a browser that lives for a year would otherwise
    // accumulate a key per prompt per day for ever.
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const k = window.localStorage.key(i);
      if (!k || !k.startsWith(PREFIX) || k === key) continue;
      if (Number(window.localStorage.getItem(k)) <= now) window.localStorage.removeItem(k);
    }
  } catch {
    // Storage refused. The dialog simply asks again on the next load, which is
    // the safe direction to fail in.
  }
}
